import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { markdown } from "markdown";
import {
  ArrowLeftIcon,
  BellIcon,
  BellSlashIcon,
  PencilIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";

import { User, UserJSON } from "../models/users";
import { Comment } from "../models/comments";
import { ContentSectionAlt } from "../Layout/ContentSectionAlt";
import { api } from "../api";
import { ForumTopicJSON } from "../models/forum_topics";
import { LeaveAComment, commentReplyHandle } from "../Comments/LeaveAComment";
import { CommentComponent } from "../DesignSystem/CommentComponent";
import { CommentJSON } from "../models/comments";
import { useUser } from "../state";
import { AvatarList } from "../DesignSystem/AvatarList";
import { RLink } from "../DesignSystem/RLink";
import { LinkToUser } from "../DesignSystem/LinkToUser";
import { SmallButton } from "../DesignSystem/SmallButton";

type ForumTopicParams = { id: string };

type TopicComment = {
  user: UserJSON;
  comment: CommentJSON;
  likes: number;
  is_liked: boolean;
  author_banned: boolean;
};

export const ForumTopicPage = (): JSX.Element => {
  const { id } = useParams<ForumTopicParams>();
  const userState = useUser();
  const currentUser =
    userState.type === "logged-in" ? userState.user : undefined;

  const [topic, setTopic] = useState<ForumTopicJSON>();
  const [topicUser, setTopicUser] = useState<UserJSON>();
  const [subscribed, setSubscribed] = useState<boolean>();
  const [editingTopic, setEditingTopic] = useState(false);
  const [comments, setComments] = useState<TopicComment[]>([]);
  const [authorBanned, setAuthorBanned] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchTopic = async () => {
      const results = await api.get<"/api/forum/topic/:id">(
        `/api/forum/topic/${id}`
      );
      if (results.data && !cancelled) {
        setTopic(results.data.topic);
        setTopicUser(results.data.user);
        setComments(results.data.comments);
        setSubscribed(results.data.subscribed);
        setAuthorBanned(results.data.author_banned ?? false);
      }
    };

    fetchTopic();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const isYours = useMemo(
    () =>
      currentUser && topicUser && currentUser.username === topicUser.username,
    [currentUser, topicUser]
  );

  const editField =
    <T extends keyof ForumTopicJSON>(field: T) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!topic) return;
      setTopic({
        ...topic,
        [field]: event.currentTarget.value,
      });
    };

  const saveEdits = async () => {
    if (!topic) {
      return;
    }

    const result = await api.put<"/api/forum/topic/:id">(
      `/api/forum/topic/${id}`,
      {
        title: topic.title,
        body: topic.body,
        board: topic.board,
      }
    );

    if (result.type === "error") {
      setEditingTopic(false);
      return;
    }

    setEditingTopic(false);
    setTopic(result.data);
  };

  const toggleSubscribe = async (value: boolean) => {
    if (!topic) {
      return;
    }

    if (value) {
      const res = await api.post<"/api/forum/subscription/:topic_id">(
        `/api/forum/subscription/${topic.id}`
      );
      if (api.isErrorToast(res)) {
        return;
      }
    } else {
      const res = await api.delete<"/api/forum/subscription/:topic_id">(
        `/api/forum/subscription/${topic.id}`
      );
      if (api.isErrorToast(res)) {
        return;
      }
    }

    setSubscribed(value);
  };

  if (!topic || !topicUser) {
    return (
      <ContentSectionAlt>
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500 dark:text-slate-400">Loading...</div>
        </div>
      </ContentSectionAlt>
    );
  }

  return (
    <ContentSectionAlt>
      {/* Header */}
      <div className="mb-6">
        <RLink
          to="/forum"
          className="mb-3 inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to forum
        </RLink>

        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="mb-1 inline-block rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
              {topic.board}
            </span>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
              {topic.title}
            </h1>
          </div>

          {/* Subscription toggle */}
          {currentUser && (
            <SmallButton
              onClick={() => toggleSubscribe(!subscribed)}
              variant="blue"
              active={subscribed}
              icon={
                subscribed ? (
                  <BellIcon className="h-4 w-4" />
                ) : (
                  <BellSlashIcon className="h-4 w-4" />
                )
              }
            >
              {subscribed ? "Subscribed" : "Subscribe"}
            </SmallButton>
          )}
        </div>
      </div>

      {/* Original post card */}
      <div className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-800">
        {authorBanned && (
          <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400">
            The author of this post is banned. Only moderators can see this.
          </div>
        )}
        <div className="flex gap-4 p-4">
          <div className="flex-shrink-0">
            <AvatarList users={[topicUser]} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                <LinkToUser linkUser={topicUser} />
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {new Date(topic.created).toDateString()}
              </span>
            </div>

            {editingTopic ? (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Title
                  </label>
                  <input
                    onChange={editField("title")}
                    type="text"
                    defaultValue={topic.title}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Board
                  </label>
                  <select
                    onChange={(e) =>
                      setTopic({ ...topic, board: e.target.value })
                    }
                    defaultValue={topic.board}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="chips">Chips</option>
                    <option value="memes">Memes</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Body
                  </label>
                  <textarea
                    onChange={editField("body")}
                    defaultValue={topic.body}
                    rows={6}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  />
                </div>
              </div>
            ) : (
              <div
                className="prose max-w-none text-slate-700 dark:text-slate-200"
                dangerouslySetInnerHTML={{
                  __html: markdown.toHTML(topic.body || ""),
                }}
              />
            )}
          </div>
        </div>

        {/* Edit controls */}
        {(isYours || (currentUser && currentUser.admin)) && (
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-white/10 dark:bg-slate-700/50">
            {editingTopic ? (
              <SmallButton
                onClick={saveEdits}
                variant="primary"
                icon={<CheckIcon className="h-4 w-4" />}
              >
                Save Changes
              </SmallButton>
            ) : (
              <SmallButton
                onClick={() => setEditingTopic(true)}
                variant="tertiary"
                icon={<PencilIcon className="h-4 w-4" />}
              >
                Edit
              </SmallButton>
            )}
          </div>
        )}
      </div>

      {/* Comments section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h2>

        {comments.map(({ comment, user, likes, is_liked, author_banned }) => (
          <CommentComponent
            key={comment.id}
            anonymous={"never"}
            replyCB={(username) => commentReplyHandle.replyTo(username)}
            comment_user={new User(user)}
            comment_likes={likes}
            comment_is_liked={is_liked}
            comment={new Comment(comment)}
            author_banned={author_banned}
          />
        ))}

        <LeaveAComment
          canPostAnonymously={false}
          topic={topic}
          showSubscriptionBox
          onAddComment={(comment) => {
            setComments((prev) => [
              ...prev,
              {
                comment: comment,
                user: currentUser as UserJSON,
                likes: 0,
                is_liked: false,
                author_banned: false,
              },
            ]);
          }}
        />
      </div>
    </ContentSectionAlt>
  );
};
